import { useAuth } from "@clerk/clerk-react";
import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AccessRequestError, createAccessSession } from "./accessApi";
import { ACCESS_DENIED_EVENT, POLICY_EPOCH_EVENT, type PolicyEpochEventDetail } from "./events";
import type { Capability } from "./permissions";
import { getIntelligenicToken } from "@/auth/getIntelligenicToken";
import type {
    AccessContextValue, AccessEnforcementMode, AccessLevel, AccessProviderProps, AccessStatus
} from "./types";

interface AccessState {
    productId: number | null;
    permissions: ReadonlySet<Capability>;
    allowedOperations: ReadonlySet<string>;
    accessLevel: AccessLevel;
    status: AccessStatus;
    isRefreshing: boolean;
    error: Error | null;
    systemPolicyEpoch: number | null;
    tenantPolicyEpoch: number | null;
    policyEpoch: string | null;
    enforcementMode: AccessEnforcementMode | null;
    requestId: string | null;
    expiresAt: string | null;
}

const EMPTY_PERMISSIONS = new Set<Capability>();
const EMPTY_OPERATIONS = new Set<string>();

const deniedState = (status: AccessStatus, error: Error | null = null, requestId: string | null = null): AccessState => ({
    productId: null,
    permissions: EMPTY_PERMISSIONS,
    allowedOperations: EMPTY_OPERATIONS,
    accessLevel: null,
    status,
    isRefreshing: false,
    error,
    systemPolicyEpoch: null,
    tenantPolicyEpoch: null,
    policyEpoch: null,
    enforcementMode: null,
    requestId,
    expiresAt: null
});

export const AccessContext = createContext<AccessContextValue | undefined>(undefined);

export const AccessProvider: React.FC<AccessProviderProps> = ({
    productId,
    surface = "editor.mutations",
    workspaceBinding = productId ? `web-editor:${productId}` : "web-editor:none",
    pollIntervalMs = 30_000,
    children
}) => {
    const { getToken, isLoaded, isSignedIn, userId } = useAuth();
    const [refreshVersion, setRefreshVersion] = useState(0);
    const [state, setState] = useState<AccessState>(() => deniedState("loading"));
    const lastDeniedRefresh = useRef(0);

    const refresh = useCallback(() => setRefreshVersion(version => version + 1), []);

    useEffect(() => {
        const controller = new AbortController();
        if (!isLoaded) {
            setState(deniedState("loading"));
            return () => controller.abort();
        }
        if (!isSignedIn || !userId || productId === null) {
            setState(deniedState("denied"));
            return () => controller.abort();
        }
        if (!Number.isInteger(productId) || productId <= 0) {
            setState(deniedState("error", new Error("Product ID is invalid.")));
            return () => controller.abort();
        }

        // Keep a still-valid snapshot during background refresh. Product/user
        // changes have no reusable authority and return to deny-while-loading.
        setState(previous => previous.status === "ready" && previous.productId === productId
            ? { ...previous, isRefreshing: true, error: null }
            : deniedState("loading"));

        void (async () => {
            try {
                const token = await getIntelligenicToken(getToken);
                if (!token) throw new AccessRequestError("Authentication token is unavailable.", 401);
                const response = await createAccessSession(productId, token, surface, workspaceBinding, controller.signal);
                if (controller.signal.aborted) return;
                setState({
                    productId,
                    permissions: new Set(response.permissions),
                    allowedOperations: new Set(response.allowedOperations),
                    accessLevel: response.accessLevel,
                    status: "ready",
                    isRefreshing: false,
                    error: null,
                    systemPolicyEpoch: response.systemPolicyEpoch,
                    tenantPolicyEpoch: response.tenantPolicyEpoch,
                    policyEpoch: response.policyEpoch,
                    enforcementMode: response.enforcementMode,
                    requestId: response.requestId,
                    expiresAt: response.expiresAt
                });
            } catch (error) {
                if (controller.signal.aborted) return;
                const normalized = error instanceof Error ? error : new Error("Access policy failed.");
                const denied = normalized instanceof AccessRequestError && (normalized.status === 403 || normalized.status === 404);
                setState(deniedState(
                    denied ? "denied" : "error",
                    denied ? null : normalized,
                    normalized instanceof AccessRequestError ? normalized.requestId : null
                ));
            }
        })();
        return () => controller.abort();
    }, [getToken, isLoaded, isSignedIn, productId, refreshVersion, surface, userId, workspaceBinding]);

    useEffect(() => {
        if (pollIntervalMs <= 0) return;
        const timer = window.setInterval(refresh, pollIntervalMs);
        return () => window.clearInterval(timer);
    }, [pollIntervalMs, refresh]);

    useEffect(() => {
        const onFocus = () => refresh();
        const onDenied = () => {
            const now = Date.now();
            if (now - lastDeniedRefresh.current < 5_000) return;
            lastDeniedRefresh.current = now;
            refresh();
        };
        const onEpoch = (event: Event) => {
            const next = (event as CustomEvent<PolicyEpochEventDetail>).detail?.policyEpoch;
            if (next && next !== state.policyEpoch) refresh();
        };
        window.addEventListener("focus", onFocus);
        window.addEventListener(ACCESS_DENIED_EVENT, onDenied);
        window.addEventListener(POLICY_EPOCH_EVENT, onEpoch);
        return () => {
            window.removeEventListener("focus", onFocus);
            window.removeEventListener(ACCESS_DENIED_EVENT, onDenied);
            window.removeEventListener(POLICY_EPOCH_EVENT, onEpoch);
        };
    }, [refresh, state.policyEpoch]);

    useEffect(() => {
        if (!state.expiresAt) return;
        const remaining = Date.parse(state.expiresAt) - Date.now();
        const timer = window.setTimeout(refresh, Math.max(0, remaining));
        return () => window.clearTimeout(timer);
    }, [refresh, state.expiresAt]);

    const value = useMemo<AccessContextValue>(() => {
        const snapshotUnexpired = state.expiresAt !== null && Date.parse(state.expiresAt) > Date.now();
        const current = state.status === "ready" && state.productId === productId && snapshotUnexpired;
        const hasPermission = (permission: Capability) => current && state.permissions.has(permission);
        const hasAllPermissions = (permissions: readonly Capability[]) =>
            current && permissions.length > 0 && permissions.every(permission => state.permissions.has(permission));
        return {
            productId,
            permissions: current ? state.permissions : EMPTY_PERMISSIONS,
            allowedOperations: current ? state.allowedOperations : EMPTY_OPERATIONS,
            accessLevel: current ? state.accessLevel : null,
            status: state.status,
            isLoading: state.status === "loading",
            isRefreshing: state.isRefreshing,
            isAllowed: current,
            error: state.error,
            systemPolicyEpoch: current ? state.systemPolicyEpoch : null,
            tenantPolicyEpoch: current ? state.tenantPolicyEpoch : null,
            policyEpoch: current ? state.policyEpoch : null,
            enforcementMode: current ? state.enforcementMode : null,
            requestId: state.requestId,
            expiresAt: current ? state.expiresAt : null,
            hasPermission,
            hasAllPermissions,
            hasOperation: operation => current && state.allowedOperations.has(operation),
            refresh
        };
    }, [productId, refresh, state]);

    return <AccessContext.Provider value={value}>{children}</AccessContext.Provider>;
};
